import { unwrap, type components } from "@sagot/api-client";
import { useMutation } from "@tanstack/react-query";
import { useApi } from "../../../../session/session-provider";
import type { AssistantOutcome } from "@sagot/constants";
import type { Definition, DryRunResult, PreviewOutcomeResult, PreviewStart } from "../types";

type PreviewBody = components["schemas"]["WorkflowPreviewIn"];
type DryRunBody = components["schemas"]["WorkflowDryRunIn"];

export interface PreviewRequest {
  /** Omitted: the server picks the workflow's own agent, else the first one the member may test. */
  agentId?: string;
  /** The definition as the builder shows it, unsaved edits included. */
  definition: Definition;
}

export interface DryRunRequest {
  definition: Definition;
  channel: DryRunBody["channel"];
  officeHours: boolean;
  message: string;
}

/** `POST /v1/workflows/{id}/preview`: a Playground conversation that runs the draft. */
export function usePreviewStart(workflowId: string) {
  const api = useApi();
  return useMutation<PreviewStart, Error, PreviewRequest>({
    mutationFn: ({ agentId, definition }) =>
      unwrap(
        api.POST("/v1/workflows/{workflow_id}/preview", {
          params: { path: { workflow_id: workflowId } },
          body: { agent_id: agentId ?? null, definition: { ...definition } } satisfies PreviewBody,
        }),
      ),
  });
}

export interface PreviewOutcomeRequest {
  conversationId: string;
  runId: string;
  outcome: AssistantOutcome;
}

/** `POST /v1/workflows/{id}/preview/outcome`: how the assistant's answer goes in a Preview run that waits for it. */
export function usePreviewOutcome(workflowId: string) {
  const api = useApi();
  return useMutation<PreviewOutcomeResult, Error, PreviewOutcomeRequest>({
    mutationFn: ({ conversationId, runId, outcome }) =>
      unwrap(
        api.POST("/v1/workflows/{workflow_id}/preview/outcome", {
          params: { path: { workflow_id: workflowId } },
          body: { conversation_id: conversationId, run_id: runId, outcome },
        }),
      ),
  });
}

/** `POST /v1/workflows/{id}/dry-run`: the steps a background workflow would take; nothing runs. */
export function useDryRun(workflowId: string) {
  const api = useApi();
  return useMutation<DryRunResult, Error, DryRunRequest>({
    mutationFn: ({ definition, channel, officeHours, message }) =>
      unwrap(
        api.POST("/v1/workflows/{workflow_id}/dry-run", {
          params: { path: { workflow_id: workflowId } },
          body: { definition: { ...definition }, channel, office_hours: officeHours, message: message.trim() || null } satisfies DryRunBody,
        }),
      ),
  });
}
